<template>
  <div
    v-if="status?.is_employee"
    class="work-schedule-status">
    <a-alert
      v-if="status.editing.needs_filling"
      class="work-schedule-status__reminder"
      type="warning"
      show-icon
      :message="`Заполните график работы на ${nextMonth}`">
      <template #description>
        Сделать это можно до {{ formatDate(status.editing.closes_at) }} включительно.
        После блокировки график меняет сотрудник с правом редактирования графиков.
      </template>
      <template #action>
        <a-button
          size="small"
          type="primary"
          @click="openSchedule">
          Заполнить
        </a-button>
      </template>
    </a-alert>

    <a-card
      size="small"
      class="work-schedule-status__card"
      :class="{ 'work-schedule-status__card--empty': !status.current.is_filled }">
      <template #title>
        Мой график: {{ currentMonth }}
      </template>
      <template #extra>
        <a @click.prevent="openSchedule">Открыть</a>
      </template>

      <div
        v-if="!status.current.is_filled"
        class="work-schedule-status__empty">
        <exclamation-circle-outlined />
        График на {{ currentMonth.toLowerCase() }} не заполнен
      </div>

      <template v-else>
        <div class="work-schedule-status__today">
          Сегодня: <b>{{ dayText(status.current.today) }}</b>
          <span class="work-schedule-status__totals">
            {{ status.current.totals.working_days_count }} раб. дн.,
            {{ formatDuration(status.current.totals.working_minutes) }} за месяц
          </span>
        </div>

        <div
          v-if="upcoming.length"
          class="work-schedule-status__upcoming">
          <span
            v-for="day in upcoming"
            :key="day.date"
            class="work-schedule-status__day">
            <span class="work-schedule-status__date">{{ shortDate(day.date) }}</span>
            <span :class="{ 'work-schedule-status__off': day.is_day_off }">{{ dayText(day) }}</span>
          </span>
        </div>
      </template>
    </a-card>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ExclamationCircleOutlined } from '@ant-design/icons-vue';
import { api } from '@/api';
import {
  dayText, formatDate, formatDuration, periodLabel, shortDate,
} from '@/Components/CRM/WorkSchedule/workScheduleFormat';

const UPCOMING_LIMIT = 4;

const router = useRouter();

const status = ref(null);

const currentMonth = computed(() => (status.value ? periodLabel(status.value.current.period) : ''));
const nextMonth = computed(
  () => (status.value ? periodLabel(status.value.editing.period).toLowerCase() : ''),
);

const upcoming = computed(() => {
  const days = status.value?.current.upcoming ?? [];
  const today = status.value?.current.today;

  return days
    .filter((day) => day.date !== today?.date)
    .slice(0, UPCOMING_LIMIT);
});

const openSchedule = () => router.push('/crm/work-schedule');

onMounted(async () => {
  try {
    const { data } = await api.getMyWorkScheduleStatus();
    status.value = data;
  } catch {
    status.value = null;
  }
});
</script>

<style lang="scss" scoped>
.work-schedule-status {
  margin-bottom: 16px;

  &__reminder {
    margin-bottom: 12px;
  }

  &__card--empty {
    border-color: #ffbb96;
    background: #fff7e6;
  }

  &__empty {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #d46b08;
  }

  &__today {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 12px;
  }

  &__totals {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  &__upcoming {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    margin-top: 8px;
    font-size: 12px;
  }

  &__day {
    display: flex;
    gap: 6px;
  }

  &__date {
    color: rgba(0, 0, 0, 0.45);
  }

  &__off {
    color: rgba(0, 0, 0, 0.35);
  }
}
</style>
